import AppHelper from '@Common/ApplicationCore/AppHelper';
import documentNameProvider from '@Common/ApplicationCore/DocumentNameProvider';

import { isChartComponent, isGridComponent } from '@Common/Core/ComponentsTraits';
import constants from '@Common/Core/Constant';
import { DashboardSettings } from '@Common/Core/DashboardSettings';
import Maybe from '@Common/Core/Maybe';
import { properties } from '@Common/Core/Properties';
import { ServiceProvider } from '@Common/Core/ServiceProvider.generated';
import { ConfirmationDialog, ConfirmationResult } from '@Common/Dialogs/Confirmation';
import { MessageBox, MessageBoxIcons } from '@Common/Dialogs/MessageBox';
import {
    AggregationType,
    ApplicationType,
    ChartDateType,
    ComponentType,
    DayOfWeek,
    EntityDriverType,
    InsightExceptionType,
    ReferenceLineType,
    ShowSummaryMode,
    StorageContainerType,
    SubAggregationMode
} from '@Common/Enums';
import * as commonUtils from '@Common/Helpers/CommonUtils';
import documentHelper from '@Common/Helpers/DocumentHelper';
import documentUtils from '@Common/Helpers/DocumentUtils';
import errorHelper from '@Common/Helpers/ErrorHelper';
import FontHelper from '@Common/Helpers/FontHelper';
import { ProcessMarketData } from '@Common/Helpers/ProcessMarketData';
import timeHelper from '@Common/Helpers/TimeHelper';
import urlHelper from '@Common/Helpers/UrlHelper';

import {
    AbstractInstanceItem,
    AdminModel,
    AggregationDriver,
    BaseComponent,
    BaseDataDefinition,
    BaseDefinition,
    BaseModel,
    BaseSeries,
    BookmarkDocument,
    Categorized,
    ChartAppearance,
    Component,
    ComponentData,
    ConnectionInformation,
    DashboardSnapshot,
    DataLoadIdentifier,
    DataRequestConfiguration,
    DateTimeInterval,
    DBMapping,
    DBMappingEmbededDataSource,
    DimensionFilterValues,
    DocumentBase,
    DocumentByReference,
    DocumentDescriptor,
    DocumentReferenceBase,
    DocumentReferences,
    DocumentWithChilds,
    DynamicMenuResult,
    EntityDriver,
    FindBestGroupResult,
    GridPrintSettings,
    GroupFilter,
    ImportProjectOptions,
    InsightObject,
    MapData,
    MapParameter,
    MenuSearchResult,
    MetricPool,
    MetricSeries,
    ProcessChartData,
    ProcessConfiguration,
    ProjectDefinition,
    ProjectImportResult,
    RecordDefinition,
    RecordDimensionConfiguration,
    RecordDocument,
    RecordSeries,
    Report,
    ReportSubscription,
    SaveDocumentResponce,
    SchedulePlanResponce,
    SwimlaneChartData,
    TableUpdater,
    TaskItem,
    TimeSettings,
    TrellisProcessChartData,
    TrellisSwimlaneChartData,
    ViewComponent,
    ViewerModel,
    ViewObject
} from '@InstanceFactory';
import dbMapperControlHelper from '@Studio/Controls/DBMapping/DBMappingHelper';
import { DocumentBuilder } from '@Studio/Core/DocumentBuilder';
import { Base } from '@View/Components/Base';
import { FiltrationProvider } from '@View/Core/FiltrationProvider';
import { FilterGroup } from '@View/Helpers/FilterHelper';
import formatHelper from '@Common/Helpers/FormatHelper';
import ColorHelper from '@Common/Helpers/ColorHelper';
import { delocalize, getRequestObject, getRequestObjectWithSpecTypes } from '@Common/Helpers/JsonUtils';
import Guid from '@Common/Core/Guid';
import { isTimeWindow } from './ComponentsTraits';
import StylesHelper from '@Common/Helpers/StylesHelper';
import localizationHelper from '@Common/Helpers/LocalizationHelper';

export interface IDataAccess {
    dataServiceProvider: DataServiceProvider;
    projectId: Guid;
}

export type DataParameter<T> = {
    dataRequestConfiguration: Partial<T>;
    projectId: Guid;
    control: Base;
    component: Component;
    permanentRequest?: boolean;
    isEditor?: boolean;
    isLoggingEnabled?: boolean;
    markerFilterData?: DimensionFilterValues[];
    isTrellis?: boolean;
    requestMarker?: string;
    viewFilters?: GroupFilter[];
    affectedDriverGroups?: FilterGroup[];
    parentIds?: string[];
    parentId?: string;
    selfGroupFilter?: GroupFilter;
};

export interface IProcessDataParam {
    projectId: Guid;
    configuration: ProcessConfiguration;
    control: Base;
    viewFilters: GroupFilter[];
    parentId: Guid;
}

type ComponentParameter = DataRequestConfiguration;

type AnyParam = MapParameter | ComponentParameter;

type RequestIdentifier = {
    componentId: Guid;
    marker: string;
    requestId: Guid;
};

function getEmptyComponentData(componentId: string = null): ComponentData {
    return ComponentData.create({
        id: componentId
    });
}

// tslint:disable: lines-between-class-members
export class DataServiceProvider extends ServiceProvider {
    private readonly getDataRequests: RequestIdentifier[] = [];
    private request: any = {};
    protected readonly isViewer: boolean = false;
    public readonly filtrationProvider: FiltrationProvider;

    private readonly documentsCache = {
        contains: (projectId: Guid, documentId: Guid): false => false,
        addToCache: (projectId: Guid, document: DocumentBase, built: boolean): void => _.noop(),
        getFromCache: (projectId: Guid, documentId: Guid): null => null,
        isBuilt: (projectId: Guid, documentId: Guid): false => false,
        markBuilt: (projectId: Guid, documentId: Guid): void => _.noop(),
        removeFromCache: (projectId: Guid, documentId: Guid): void => _.noop(),
        getCacheItem: (projectId: Guid, documentId: Guid): undefined => undefined,
        getItemId: (projectId: Guid, documentId: Guid): string => projectId + ':' + documentId
    };

    constructor({ isView = false, filtrationProvider = new FiltrationProvider(), tenantId = undefined } = {}) {
        super(tenantId);
        this.isViewer = isView;
        this.filtrationProvider = filtrationProvider;
        this.startCheckingSession().shutdown();
    }

    private beforeDataRequest(parameter: Partial<DataParameter<ComponentParameter>>): string {
        _.remove(
            this.getDataRequests,
            getDataRequest => getDataRequest.componentId === parameter.control.getUniqueId() && getDataRequest.marker === parameter.requestMarker
        );

        const requestId = Guid.newGuid();
        this.getDataRequests.push({
            componentId: parameter.control.getUniqueId(),
            marker: parameter.requestMarker,
            requestId: requestId
        });

        return requestId;
    }

    private getRequestIdentifier(requestId: string): RequestIdentifier {
        return _.remove<RequestIdentifier>(this.getDataRequests, getDataRequest => getDataRequest.requestId === requestId)[0];
    }

    public loadModel(applicationType?: ApplicationType): JQueryPromise<BaseModel>;
    public loadModel(applicationType: ApplicationType = altosoft.appId): JQueryPromise<BaseModel> {
        if (!altosoft.model) {
            return this.getModel(applicationType);
        } else {
            return super
                .loadModelChangeTime()
                .then(result => (altosoft.globalModelTime = result))
                .then(modelTime => altosoft.model && altosoft.globalModelTime !== modelTime && this.getModel(applicationType));
        }
    }

    public getModel(applicationType: ApplicationType = altosoft.appId, tenantId: string = this.tenantId): JQueryPromise<BaseModel> {
        return super.getModel(applicationType, tenantId).then((model: ViewerModel & AdminModel) => {
            altosoft.model = model;
            if (window.location.search.contains('UIASADMIN')) {
                altosoft.model.user.isAdmin = true;
            }
            preprocessModel(model);
            AppHelper.projects = altosoft.appId === ApplicationType.admin ? model.data.projects : model.user.projects;
            return $.Deferred<BaseModel>(def => {
                FontHelper.loadFontFamilies().then(() => def.resolve(model));
            }).promise();
        });
    }

    public loadDocumentsByCategory(
        projectId: Guid,
        documentCategories: string[],
        dontShowErrorDialog = false
    ): JQueryPromise<DocumentBase[]> {
        this.config({ dontShowErrorDialog });
        return super.loadDocumentsByCategory(projectId, documentCategories).then(
            documents => {
                this.addAccessorsAndFillReferences(projectId, documents);
                return documents;
            },
            () => (dontShowErrorDialog ? Promise.resolve([]) : undefined)
        );
    }

    public getDescriptorsByCategories(projectId: Guid, documentCategories: string[]): JQueryPromise<DocumentDescriptor[]> {
        this.request.descriptorsRequestId = Guid.newGuid();
        const currentRequestId = this.request.descriptorsRequestId;
        return super.getDescriptorsByCategoryies(projectId, documentCategories).then((descriptors: any[]) => {
            if (this.request.descriptorsRequestId === currentRequestId) {
                return $.Deferred<DocumentDescriptor[]>().resolve(descriptors).promise();
            } else {
                return $.Deferred<DocumentDescriptor[]>().promise();
            }
        });
    }

    public loadDocument(projectId: Guid, documentId: Guid, documentName: string, categories: string[]): JQueryPromise<DocumentBase> {
        if (this.documentsCache.contains(projectId, documentId)) {
            const document = this.documentsCache.getFromCache(projectId, documentId);
            if (this.documentsCache.isBuilt(projectId, documentId)) {
                return $.Deferred<DocumentBase>().resolve(document).promise();
            } else {
                return this.addAccessorsAndFillReferences(projectId, [document]).then(documents => documents[0]);
            }
        } else {
            return super
                .loadDocument(projectId, documentId, documentName, categories)
                .then(document => this.addAccessorsAndFillReferences(projectId, [document]).then(docs => docs[0]));
        }
    }

    public loadDocumentsByReferences(
        projectId: Guid,
        references: DocumentReferenceBase[],
        loadReferencedDocuments?: boolean
    ): JQueryPromise<DocumentByReference[]> {
        const cachedRefs = _.filter(references, r => this.documentsCache.contains(projectId, r.id));
        const result: DocumentByReference[] = _.map(cachedRefs, r => ({
            reference: r,
            document: this.documentsCache.getFromCache(projectId, r.id)
        }));
        references = _.differenceWith(references, cachedRefs, _.isEqual);

        if (references.length) {
            return super.tryLoadDocumentsByReferences(projectId, references).then(documents => {
                const [emptyRefs, withRefs] = _.partition(documents, dr => !dr.document);
                if (emptyRefs.length) {
                    for (const docEmpty of emptyRefs) {
                        commonUtils.log(`Can not load referenced document with id: ${docEmpty.reference.id} name: ${docEmpty.reference.name}`);
                    }
                    commonUtils.log('Warning: Some referenced documents was nod loaded. This can cause some problems.');
                }
                documents = withRefs;

                result.addRange(documents);
                if (loadReferencedDocuments) {
                    const notBuildedDocs = _(result)
                        .filter(dr => !this.documentsCache.isBuilt(projectId, dr.document.id))
                        .map(dr => dr.document)
                        .value();
                    return this.addAccessorsAndFillReferences(projectId, notBuildedDocs).then(() => result);
                } else {
                    this.addAccessors(_.map(documents, dr => dr.document));
                    _(documents).forEach(dbr => this.documentsCache.addToCache(projectId, dbr.document, false));
                    return result;
                }
            });
        } else {
            if (loadReferencedDocuments) {
                const docs = _(result)
                    .filter(dr => !this.documentsCache.isBuilt(projectId, dr.document.id))
                    .map(dr => dr.document)
                    .value();
                this.addAccessorsAndFillReferences(projectId, docs);
            }
            return $.Deferred<DocumentByReference[]>().resolve(result).promise();
        }
    }

    public tryLoadDocument(projectId: Guid, documentId: Guid, documentName: string, documentCategory: string[]): JQueryPromise<DocumentBase> {
        if (this.documentsCache.contains(projectId, documentId)) {
            const document = this.documentsCache.getFromCache(projectId, documentId);
            if (this.documentsCache.isBuilt(projectId, documentId)) {
                return $.Deferred<DocumentBase>().resolve(document).promise();
            } else {
                return this.addAccessorsAndFillReferences(projectId, [document]).then(docs => _.first(docs));
            }
        } else {
            return super
                .tryLoadDocument(projectId, documentId, documentName, documentCategory)
                .then(doc => (doc && this.addAccessorsAndFillReferences(projectId, [doc]).then(docs => _.first(docs))) || null);
        }
    }

    public loadDocumentByReference(
        projectId: Guid,
        documentReference: DocumentReferenceBase | { id; name; document?; $type? },
        updateIfLoaded = false
    ): JQueryPromise<DocumentBase> {
        if (documentReference.document && !updateIfLoaded) {
            return $.Deferred<DocumentBase>().resolve(documentReference.document).promise();
        } else {
            return this.loadDocument(projectId, documentReference.id, documentReference.name, [
                documentUtils.getDocumentCategory(documentReference)
            ]).then(document => (documentReference.document = document));
        }
    }

    public tryLoadDocumentByReference(
        projectId: Guid,
        documentReference: DocumentReferenceBase | { id; name; document?; $type? },
        updateIfLoaded = false
    ): JQueryPromise<DocumentBase> {
        if (documentReference.document && !updateIfLoaded) {
            return $.Deferred<DocumentBase>().resolve(documentReference.document).promise();
        } else {
            return this.tryLoadDocument(projectId, documentReference.id, documentReference.name, [
                documentUtils.getDocumentCategory(documentReference)
            ]).then(document => (documentReference.document = document));
        }
    }

    public saveDocument(projectId: Guid, document: DocumentBase): JQueryPromise<any> {
        this.config({ needPrepareRequest: true });
        return super.saveDocument(projectId, document).then(response => {
            this.refreshDocumentDefinitions(projectId, response.changedDocuments);
            this.documentsCache.addToCache(projectId, document, false);
            return response.lastChangedTime;
        });
    }

    public saveDocumentDescriptor(projectId: Guid, document: DocumentDescriptor): JQueryPromise<void> {
        return super.saveDocumentDescriptor(projectId, document).then(() => {
            document.isNew = false;
            this.documentsCache.removeFromCache(projectId, document.id);
            documentNameProvider.refresh(document, projectId);
        });
    }

    public renameDocument(projectId: Guid, document: DocumentDescriptor): JQueryPromise<void> {
        return super.renameDocument(projectId, document).then(() => {
            document.isNew = false;
            this.documentsCache.removeFromCache(projectId, document.id);
            documentNameProvider.refresh(document, projectId);
        });
    }

    public tryDeleteDocuments(projectId: Guid, documentIds: Guid[]): JQueryPromise<DocumentReferences[]> {
        return super.tryDeleteDocuments(projectId, documentIds).then(usages => {
            documentIds.forEach(id => {
                if (!usages || !usages.firstOrNull(u => u.currentDocumentId === id)) {
                    this.documentsCache.removeFromCache(projectId, id);
                }
            });
            return usages;
        });
    }

    public importProjectData(projectId: Guid, importOptions: ImportProjectOptions): JQueryPromise<ProjectImportResult> {
        const deferred = $.Deferred<ProjectImportResult>();
        let debugFirstTime = true;

        super.startImportProjectData(projectId, importOptions).then(operationGuid => {
            const importCheck = () => {
                this.getImportResult(projectId, operationGuid).then(
                    importResult => {
                        const messageDescriptor = errorHelper.getInsightVersionCheckResult(importResult.insightVersionCheckResult);

                        if (messageDescriptor) {
                            if (messageDescriptor.isFatal) {
                                deferred.reject({ message: messageDescriptor.message });
                            } else {
                                new ConfirmationDialog({ text: messageDescriptor.message }).show().then(result => {
                                    if (result === ConfirmationResult.yes) {
                                        this.importProjectData(projectId, _.assign(importOptions, { useForceUpdate: true }))
                                            .progress(data => deferred.notify(data))
                                            .then(
                                                importRes => deferred.resolve(importRes),
                                                error => deferred.reject(error)
                                            );
                                    } else {
                                        deferred.resolve(null);
                                    }
                                });
                            }
                            return;
                        }

                        if (importResult.isCompleted && !debugFirstTime) {
                            deferred.resolve(importResult);
                        } else {
                            debugFirstTime = false;
                            deferred.notify(importResult);
                            setTimeout(importCheck, 1000);
                        }
                    },
                    error => deferred.reject(error)
                );
            };
            importCheck();
        });

        return deferred.promise();
    }

    public createProject(
        project: ProjectDefinition,
        dataConnectionInfo: ConnectionInformation,
        stagingConnectionInfo: ConnectionInformation,
        importProjectZipPath: string,
        overrideIfExists: boolean,
        tenantId?: string
    ): JQueryPromise<ProjectImportResult> {
        const deferred = $.Deferred<ProjectImportResult>();
        let debugFirstTime = true;

        super
            .startCreateProject(project, dataConnectionInfo, stagingConnectionInfo, importProjectZipPath, overrideIfExists, this.tenantId)
            .then(operationGuid => {
                const importCheck = () => {
                    this.getImportResult(constants.guidEmpty, operationGuid).then(
                        importResult => {
                            if (importResult.isCompleted && !debugFirstTime) {
                                deferred.resolve(importResult);
                            } else {
                                debugFirstTime = false;
                                deferred.notify(importResult);
                                setTimeout(importCheck, 1000);
                            }
                        },
                        error => deferred.reject(error)
                    );
                };
                importCheck();
            });

        return deferred.promise();
    }

    public importSolution(project: ProjectDefinition, importSolutionZipPath: string, overrideIfExists: boolean): JQueryPromise<ProjectImportResult> {
        const deferred = $.Deferred<ProjectImportResult>();
        let debugFirstTime = true;

        super.startImportSolution(project, importSolutionZipPath, overrideIfExists).then(operationGuid => {
            const importCheck = () => {
                this.getImportResult(constants.guidEmpty, operationGuid).then(
                    importResult => {
                        if (importResult.isCompleted && !debugFirstTime) {
                            deferred.resolve(importResult);
                        } else {
                            debugFirstTime = false;
                            deferred.notify(importResult);
                            setTimeout(importCheck, 1000);
                        }
                    },
                    error => deferred.reject(error)
                );
            };
            importCheck();
        });

        return deferred.promise();
    }

    public updateTableAndSave(projectId: Guid, updates: TableUpdater[], doc: DocumentBase): JQueryPromise<SaveDocumentResponce> {
        return super.updateTableAndSave(projectId, updates, doc).then(response => {
            this.refreshDocumentDefinitions(projectId, response.changedDocuments);
            doc.lastModificationDate = response.lastChangedTime;
            return response;
        });
    }

    public scheduleExecutionPlans(projectId: Guid, plans: DocumentBase[]): JQueryPromise<SchedulePlanResponce> {
        return super.scheduleExecutionPlan(projectId, plans).then(result => {
            _(plans).forEach(plan => (plan.lastModificationDate = result.lastModificationDate));
            return result;
        });
    }

    public getScheduledTasks(projectId: Guid, timeInterval: DateTimeInterval): JQueryPromise<TaskItem[]>;
    public getScheduledTasks(projectId: Guid, from: Date, to: Date): JQueryPromise<TaskItem[]>;
    public getScheduledTasks(...args: any[]): JQueryPromise<TaskItem[]> {
        const [projectId, from, to] = args.length === 2 ? [args[0], args[1].from, args[1].to] : args;
        this.config({ dontShowErrorDialog: true });
        return super.getScheduledTasks(projectId, from, to);
    }

    public stopDataLoads(projectId: Guid, parameters: DataLoadIdentifier[]): JQueryPromise<void>;
    public stopDataLoads(dataLoadIds: DataLoadIdentifier[]): JQueryPromise<void>;
    public stopDataLoads(...args: any[]): JQueryPromise<void> {
        const [projectId, parameters] = args.length === 1 ? [constants.guidEmpty, args[0]] : args;
        return super.stopDataLoads(projectId, parameters);
    }

    public copyDocument(
        projectId: Guid,
        documentId: Guid,
        documentName: string,
        newName: string,
        newParentId: Guid
    ): JQueryPromise<DocumentWithChilds> {
        return super.copyDocument(projectId, documentId, documentName, newName, newParentId).then(res => {
            this.refreshDocumentDefinitions(projectId, res.changedDocuments);
            return res;
        });
    }

    public findBestAppropriateGroup(projectId: Guid, olapCubeId: Guid, dateTimeField: string, timeLevel: string): JQueryPromise<FindBestGroupResult> {
        this.config({ dontShowErrorDialog: true });
        return super.findBestAppropriateGroup(projectId, olapCubeId, dateTimeField, timeLevel);
    }

    public loadDocumentByDescriptor(projectId: Guid, descriptor: { id: Guid; name: string; category: string }): JQueryPromise<DocumentBase> {
        return this.loadDocument(projectId, descriptor.id, descriptor.name, [descriptor.category]);
    }

    private refreshDocumentDefinitions(projectId: Guid, definitions: BaseDefinition[]): void {
        _(definitions)
            .filter(definition => !!definition)
            .forEach(definition => {
                const storedDefinitions = documentHelper.getDocuments(projectId, definition.category);
                const storedDefinition = _.find(storedDefinitions, item => item.id === definition.id);
                if (storedDefinition) {
                    storedDefinitions.remove(storedDefinition);
                }
                storedDefinitions.push(definition);
            });
    }

    private addDBMappingAccessors(dbMapping: DBMapping) {
        _(dbMapperControlHelper.getAllSources(dbMapping))
            .filter(s => dbMapperControlHelper.isEmbededFileSource(s))
            .forEach((source: DBMappingEmbededDataSource) => {
                source.sourceDocument = documentUtils.createDocumentReference(source.table);
            });
    }

    private addAccessors(documents: DocumentBase[]) {
        _(documents).forEach(document => {
            if (RecordDocument.isInstanceOfType(document) && !Object.prototype.hasOwnProperty.call(document, 'storeDataToDatabase')) {
                Object.defineProperty(document, 'storeDataToDatabase', {
                    get: function () {
                        return this.saveEventToRepository === 'Save' || this.saveEventToRepository === 'Overwrite';
                    }
                });
                this.addDBMappingAccessors(document.dbMapping);
            } else if (MetricPool.isInstanceOfType(document) && !Object.prototype.hasOwnProperty.call(document, 'items')) {
                Object.defineProperty(document, 'items', {
                    get: function () {
                        return this.template.items;
                    }
                });
            }
        });
    }

    private addAccessorsAndFillReferences(projectId: Guid, documents: DocumentBase[]): JQueryPromise<DocumentBase[]> {
        this.addAccessors(documents);

        const builder = new DocumentBuilder();
        const references = _.flatMap(documents, d => builder.getDocumentReferences(d));

        if (_.isEmpty(references)) {
            return $.Deferred<DocumentBase[]>(def => {
                def.resolve(documents);
            }).promise();
        }

        return this.loadDocumentsByReferences(projectId, references).then(loadedReferences => {
            builder.fillReferences(documents, loadedReferences);
            _.forEach(documents, doc => {
                builder.updateReferencesToInsightObjects(doc);
                this.documentsCache.addToCache(projectId, doc, true);
            });
            return documents;
        });
    }

    // --------------DataProvider-------------------------------------------------------------------------------------//
    public getMapData(parameter: DataParameter<MapParameter>): JQueryPromise<MapData>;
    public getMapData(projectId: Guid, mapParameter: MapParameter): JQueryPromise<MapData>;
    public getMapData(parameter: Guid | DataParameter<MapParameter>, mapParameter?: MapParameter): JQueryPromise<MapData> {
        if (typeof parameter === 'string') {
            return super.getMapData(parameter, mapParameter);
        }

        return this.getMapDataInner(parameter);
    }

    private getMapDataInner(parameter: DataParameter<MapParameter>): JQueryPromise<MapData> {
        this.filtrationProvider.applyMapFilters(parameter);
        const shapeRequestConfiguration = parameter.dataRequestConfiguration.shapeRequestConfiguration;
        shapeRequestConfiguration.componentDefinition = getRequestObject(shapeRequestConfiguration.componentDefinition);

        const displayShapes = this.filtrationProvider.needDisplayData(parameter, parameter.projectId, shapeRequestConfiguration);

        if (!displayShapes) {
            const emptyRes = {} as any;
            return $.when(emptyRes);
        }

        const requestId = this.beforeDataRequest(parameter);

        return super.getMapData(parameter.projectId, parameter.dataRequestConfiguration as MapParameter).then((mapData: MapData) => {
            const timeSettings = parameter.component.dataDefinition.timeSettings;
            DataServiceProvider.updateMetricLastData(parameter.projectId, timeSettings, mapData.lastAvailableDate);

            const request = this.getRequestIdentifier(requestId);
            if (request || parameter.permanentRequest) {
                return $.Deferred<MapData>().resolve(mapData).promise();
            }
            return $.Deferred<MapData>().promise();
        });
    }

    public getData(parameter: DataParameter<ComponentParameter>): JQueryPromise<ComponentData>;
    public getData(projectId: Guid, configuration: DataRequestConfiguration): JQueryPromise<ComponentData>;
    public getData(parameter: Guid | DataParameter<ComponentParameter>, configuration?: DataRequestConfiguration): JQueryPromise<ComponentData> {
        if (typeof parameter === 'string') {
            this.config({ needPrepareRequest: false });
            return super.getData(parameter, configuration);
        }

        const requestId = this.beforeDataRequest(parameter);

        let componentData = getEmptyComponentData(parameter.component.id);

        if (parameter && parameter.component.appearance.componentType === ComponentType.entityDriver) {
            componentData = this.getEntityDriverData(parameter.component as any, parameter.projectId);
            return DataServiceProvider.onGetDataCompleted(parameter, componentData);
        }

        if (parameter && parameter.component.appearance.componentType === ComponentType.aggregationDriver) {
            componentData = this.getAggregationDriverData(parameter.component as any);
            return DataServiceProvider.onGetDataCompleted(parameter, componentData);
        }

        if (!parameter.isEditor) {
            this.filtrationProvider.applyFilters(parameter);
        }

        if (!this.filtrationProvider.needDisplayData(parameter, parameter.projectId)) {
            return DataServiceProvider.onGetDataCompleted(parameter, componentData);
        }
        const dataRequestConfiguration = parameter.dataRequestConfiguration;
        if (this.isViewer) {
            dataRequestConfiguration.componentDefinition.isLoggingEnabled = parameter.isLoggingEnabled;
        }

        dataRequestConfiguration.componentDefinition = getRequestObjectWithSpecTypes(
            dataRequestConfiguration.componentDefinition,
            parameter.projectId
        );
        dataRequestConfiguration.periodItems = getRequestObject(dataRequestConfiguration.periodItems);
        if (dataRequestConfiguration.parentMetricComponentDefinition) {
            dataRequestConfiguration.parentMetricComponentDefinition = getRequestObject(dataRequestConfiguration.parentMetricComponentDefinition);
        }

        return this.getDataFromService(parameter, requestId);
    }

    private getAggregationDriverData(component: AggregationDriver): ComponentData {
        return {
            id: component.id,
            dataRows: _.map(component.aggregationTypes, aggregationType => {
                const enumValue = AggregationType.getValue(aggregationType) || { name: aggregationType };
                return {
                    dimensions: [{ key: aggregationType, value: enumValue.name, rawData: aggregationType }],
                    data: []
                };
            }),
            totalRow: { data: [], dimensions: [] }
        } as any;
    }

    private getEntityDriverData(component: EntityDriver, projectId: Guid): ComponentData {
        const rows = _.map(component.entities, entity => {
            const document =
                component.entityDriverType === EntityDriverType.record
                    ? documentHelper.findRecord(projectId, { id: entity.entity.id })
                    : documentHelper.getMetric(projectId, { id: entity.entity.id });

            return {
                dimensions: [
                    {
                        key: entity.entity.id,
                        value: entity.entityName ? entity.entityName : document != null ? document.name : entity.entity.id,
                        rawData: entity.entity.id
                    }
                ],
                data: []
            };
        });

        return { id: component.id, dataRows: rows, totalRow: { data: [], dimensions: [] } } as any;
    }

    public getRecordDimensionData(projectId: Guid, configuration: RecordDimensionConfiguration): JQueryPromise<any> {
        this.config({ dontShowErrorDialog: true });
        return super
            .getRecordDimensionData(projectId, configuration)
            .then(componentData => componentData || getEmptyComponentData())
            .then(this.dataToListItems);
    }

    public getMetricDimensionData(
        projectId: Guid,
        baseDataDefinition: BaseDataDefinition,
        pageSize = 0,
        startResultIndex = null
    ): JQueryPromise<any> {
        const dataDefinition = BaseDataDefinition.create();
        dataDefinition.timeSettings.chartDateType = ChartDateType.dateRange;
        dataDefinition.timeSettings.interval = { from: constants.minDate, to: constants.maxDate };

        const dataRequestConfiguration = DataRequestConfiguration.create();
        dataRequestConfiguration.componentDefinition = dataDefinition;
        dataRequestConfiguration.componentType = ComponentType.driver;
        dataRequestConfiguration.pageSize = pageSize;
        dataRequestConfiguration.startResultIndex = startResultIndex;

        dataDefinition.series = _.filter(baseDataDefinition.series, series => (series.inUse || series.requestData) && !series.gridSeriesOnly);
        dataDefinition.drillDowns.push(_.find(baseDataDefinition.drillDowns, dd => dd.inUse && dd.chartDimensionId === constants.dimension1));

        dataRequestConfiguration.seriesFilters = _.map(dataDefinition.series, item => ({ seriesId: item.seriesId } as any));
        dataRequestConfiguration.drillDownIds = _.map(dataDefinition.drillDowns, item => item.id);

        this.config({ dontShowErrorDialog: true });
        return super
            .getData(projectId, dataRequestConfiguration)
            .then(componentData => componentData || getEmptyComponentData())
            .then(componentData => this.dataToListItems(componentData, false));
    }

    public getRecordData(
        projectId: Guid,
        record: RecordDefinition,
        dimensionId: string,
        startResultIndex: number,
        pageSize: number
    ): JQueryPromise<any> {
        const dataDefinition = BaseDataDefinition.create();
        dataDefinition.timeSettings.chartDateType = ChartDateType.dateRange;
        dataDefinition.timeSettings.interval = { from: constants.minDate, to: constants.maxDate };
        dataDefinition.series = [
            RecordSeries.create({
                fieldPath: dimensionId,
                record: documentHelper.getReferenceByDefinition(record)
            })
        ];
        dataDefinition.displayAllRecordsOnLatestLevel = false;

        const dataRequestConfiguration = DataRequestConfiguration.create();
        dataRequestConfiguration.componentDefinition = dataDefinition;
        dataRequestConfiguration.componentType = ComponentType.driver;
        dataRequestConfiguration.startResultIndex = startResultIndex;
        dataRequestConfiguration.pageSize = pageSize;
        dataRequestConfiguration.seriesFilters = _.map(dataDefinition.series, item => ({ seriesId: item.seriesId } as any));
        dataRequestConfiguration.displayRecords = false;

        this.config({ dontShowErrorDialog: true });
        return super
            .getData(projectId, dataRequestConfiguration)
            .then(componentData => componentData || getEmptyComponentData())
            .then(this.dataToListItems);
    }

    private dataToListItems(componendData: ComponentData, isRecord = true): { id: any; value: any }[] {
        const items = componendData.dataRows.map(value => {
            const data = isRecord ? value.data[0] : value.dimensions[0];
            return {
                id: data.key,
                value: data.value || data.key
            };
        });

        return items;
    }

    public getSwimlaneData(parameter: IProcessDataParam): JQueryPromise<TrellisSwimlaneChartData | SwimlaneChartData>;
    public getSwimlaneData(projectId: Guid, configuration: ProcessConfiguration): JQueryPromise<SwimlaneChartData>;
    public getSwimlaneData(parameter: IProcessDataParam | Guid, configuration?: ProcessConfiguration): JQueryPromise<SwimlaneChartData> {
        if (typeof parameter === 'string') {
            return super.getSwimlaneData(parameter, configuration);
        }

        const requestId = this.beforeDataRequest(parameter);

        return $.Deferred<SwimlaneChartData>(def => {
            if (!this.filtrationProvider.needDisplayData(parameter, parameter.projectId)) {
                return def.resolve(SwimlaneChartData.create({}));
            }

            this.config({ dontShowErrorDialog: true });
            const promise: JQueryPromise<any> = parameter.configuration.trellisDrillDownId
                ? super.getTrellisSwimlaneData(parameter.projectId, parameter.configuration)
                : super.getSwimlaneData(parameter.projectId, parameter.configuration);

            promise.then(
                cd => {
                    if (this.getRequestIdentifier(requestId)) {
                        def.resolve(DataServiceProvider.onGetProcessDataCompleted(parameter, cd));
                    }
                },
                error => def.reject(error)
            );
        }).promise();
    }

    public getProcessChartData(parameter: IProcessDataParam): JQueryPromise<ProcessChartData | TrellisProcessChartData>;
    public getProcessChartData(projectId: Guid, configuration: ProcessConfiguration): JQueryPromise<ProcessChartData>;
    public getProcessChartData(parameter: IProcessDataParam | Guid, configuration?: ProcessConfiguration): JQueryPromise<ProcessChartData> {
        if (typeof parameter === 'string') {
            return super.getProcessChartData(parameter, configuration);
        }

        const requestId = this.beforeDataRequest(parameter);

        return $.Deferred<ProcessChartData>(def => {
            if (!this.filtrationProvider.needDisplayData(parameter, parameter.projectId)) {
                return def.resolve(ProcessChartData.create({}));
            }

            this.config({ dontShowErrorDialog: true });
            const promise: JQueryPromise<any> = parameter.configuration.trellisDrillDownId
                ? super.getTrellisProcessChartData(parameter.projectId, parameter.configuration)
                : super.getProcessChartData(parameter.projectId, parameter.configuration);

            promise.then(
                cd => {
                    if (this.getRequestIdentifier(requestId)) {
                        def.resolve(DataServiceProvider.onGetProcessDataCompleted(parameter, cd));
                    }
                },
                error => def.reject(error)
            );
        }).promise();
    }

    public getProcessGridData(parameter: IProcessDataParam): JQueryPromise<ComponentData>;
    public getProcessGridData(projectId: Guid, configuration: ProcessConfiguration): JQueryPromise<ComponentData>;
    public getProcessGridData(parameter: IProcessDataParam | Guid, configuration?: ProcessConfiguration): JQueryPromise<ComponentData> {
        if (typeof parameter === 'string') {
            return super.getProcessGridData(parameter, configuration);
        }

        const requestId = this.beforeDataRequest(parameter);

        return $.Deferred<ComponentData>(def => {
            if (!this.filtrationProvider.needDisplayData(parameter, parameter.projectId)) {
                return def.resolve(getEmptyComponentData(parameter.configuration.componentId));
            }

            this.config({ dontShowErrorDialog: true });
            super.getProcessGridData(parameter.projectId, parameter.configuration).then(
                componentData => {
                    if (this.getRequestIdentifier(requestId)) {
                        componentData = componentData || getEmptyComponentData(parameter.configuration.componentId);
                        def.resolve(DataServiceProvider.onGetProcessDataCompleted(parameter, componentData));
                    }
                },
                error => def.reject(error)
            );
        }).promise();
    }

    public getViewModel(isPrint = false, mergeInsteadOfReplace = false): JQueryPromise<void> {
        return this.getUser().then(user => {
            return this.getModelChangeTime().then(modelTime => {
                const viewType = properties.viewType;
                const bookmarkId = urlHelper.getUrlParameter(constants.bookmarkIdParameter);
                const reportId = urlHelper.getUrlParameter(constants.reportIdParameter);
                const themeId = urlHelper.getUrlParameter(constants.themeIdParameter);

                const modelLoader: JQueryPromise<ViewerModel> = commonUtils.isCacheAllowed()
                    ? super.getCachedModel(
                        altosoft.userId,
                        modelTime,
                        altosoft.locale,
                        altosoft.version,
                        viewType,
                        isPrint.toString(),
                        bookmarkId,
                        reportId,
                        themeId,
                        user.tenantId
                    )
                    : super.getViewerModel({ isPrint, viewType, bookmarkId, reportId, themeId });

                return modelLoader.then(model => {
                    model.user = user;
                    if (mergeInsteadOfReplace) {
                        _.defaults(altosoft.model, model);
                    } else {
                        altosoft.model = model;
                    }
                    preprocessModel(model);
                    AppHelper.projects = model.user.projects;
                    return $.Deferred(def => {
                        FontHelper.loadFontFamilies(document, model.insightMeta.fontFamilies).then(def.resolve, def.reject);
                    });
                });
            });
        });
    }

    public saveBookmark(bookmark: BookmarkDocument, snapshot: DashboardSnapshot, temporary: boolean): JQueryPromise<string>;
    public saveBookmark(projectId: string, bookmark: BookmarkDocument, snapshot: DashboardSnapshot, temporary: boolean): JQueryPromise<string>;
    public saveBookmark(...args: any[]): JQueryPromise<string> {
        const i = args.length === 4 ? 0 : -1;
        const projectId: string = args[i] || Guid.empty;
        const bookmark: BookmarkDocument = args[i + 1];
        const snapshot: DashboardSnapshot = args[i + 2];
        const temporary: boolean = args[i + 3];

        bookmark.ownerId = altosoft.model.user.id;
        commonUtils.visitObject(snapshot, (val: any) => {
            if (isTimeWindow(val)) {
                const { dataDefinition } = val;
                if (dataDefinition.initialTimeSettings) {
                    dataDefinition.timeSettings = dataDefinition.initialTimeSettings;
                }
            }
            if (!temporary) {
                delocalize(val);
            }
        });
        return super.saveBookmark(projectId, bookmark, snapshot, temporary);
    }

    public printReport(projectId: Guid, reportId: Guid, report: Report, viewComponent: ViewComponent, bookmarkId: Guid): JQueryPromise<string> {
        const excludeParams = _.keyBy(
            [
                constants.projectIdParameter,
                constants.viewIdParameter,
                constants.viewNameParameter,
                constants.sessionIdParameter,
                constants.bookmarkIdParameter,
                constants.reportIdParameter
            ],
            p => p.toLocaleLowerCase()
        );

        const urlParameters = _(urlHelper.urlParameters())
            .reject(item => !!excludeParams[item.key.toLocaleLowerCase()])
            .map(item => encodeURIComponent(item.key) + '=' + encodeURIComponent(item.value))
            .value();

        return super.printReport(projectId, reportId, report, viewComponent, bookmarkId, urlParameters);
    }

    public getProjectChangedTime(projectId: Guid): JQueryPromise<boolean> {
        if (!altosoft.projectLastChangedTimes) {
            altosoft.projectLastChangedTimes = {};
        }

        return super.loadProjectChangeTimes(projectId).then(result => {
            const oldValue = altosoft.projectLastChangedTimes[projectId];
            altosoft.projectLastChangedTimes[projectId] = result;
            return !(oldValue && oldValue.lastProjectChangeTime === result.lastProjectChangeTime);
        });
    }

    public getModelChangeTime(): JQueryPromise<string> {
        return super.loadModelChangeTime().then(result => (altosoft.globalModelTime = result));
    }

    public loadComponent(projectId: Guid, componentId: Guid, componentName: string, newComponent: boolean): JQueryPromise<ViewComponent> {
        const projectLastChangedTimes = altosoft.projectLastChangedTimes[projectId];

        const loadView = !newComponent;
        const viewLoader = commonUtils.isCacheAllowed()
            ? super.loadCachedView(
                altosoft.userId,
                projectLastChangedTimes ? projectLastChangedTimes.lastComponentChangeTime : '',
                altosoft.globalModelTime,
                altosoft.locale,
                altosoft.version,
                projectId,
                componentId,
                componentName || '',
                loadView
            )
            : super.loadView(projectId, componentId, componentName || '', loadView);

        return viewLoader.then(viewData => {
            const viewComponent = viewData.component;
            let isSuccess: boolean;
            if (viewComponent != null) {
                viewComponent.fullName = viewData.fullComponentName;
                isSuccess = true;
                componentId = viewComponent.id;
                componentName = viewComponent.name;
            } else {
                isSuccess = false;
            }
            if (commonUtils.isCacheAllowed() && DashboardSettings.isLogNavigation()) {
                this.addLoadedViewToLog(projectId, componentId, componentName, isSuccess);
            }
            return viewComponent;
        });
    }

    public addLoadedViewToLog(projectId: Guid, componentId: Guid, componentName: string, isSuccess: boolean): JQueryPromise<void> {
        this.config({ dontShowErrorDialog: true });
        return super.logLoadedView(projectId, componentId, componentName, isSuccess);
    }

    public getUserReportSubscriptions(projectId: Guid): JQueryPromise<ReportSubscription[]> {
        return super.getUserReportSubscriptions(projectId);
    }

    public saveUserReportSubscription(projectId: Guid, reportId: Guid, emails: string[]): JQueryPromise<any> {
        return super.saveUserReportSubscription(projectId, reportId, emails).then(
            () => true,
            () => Promise.resolve(false)
        );
    }

    public getAlertMessages(type: string): JQueryPromise<InsightObject[]> {
        this.config({
            exceptionText: strings.couldntLoadAlertMessages,
            dontShowErrorDialog: true
        });
        return super.getAllGlobalObjects([type]) as any;
    }

    public getDynamicItems(projectIds: string[]): JQueryPromise<DynamicMenuResult[]> {
        return super.getDynamicItems(projectIds).then(null, () => Promise.resolve([]));
    }

    public getMenuSearchResults(views: ViewObject[]): JQueryPromise<MenuSearchResult[]> {
        return super.getMenuSearchResults(views).then(null, () => Promise.resolve([]));
    }

    public loadProjectModel(projectId: Guid, forceLoad = false): JQueryPromise<any> {
        if (documentHelper.hasProject(projectId) && !forceLoad) {
            return $.when();
        }

        return this.getProjectChangedTime(projectId).then(changed => {
            if (documentHelper.hasProject(projectId) && !changed) {
                return;
            }

            const lastProjectChangeTime = Maybe(altosoft.projectLastChangedTimes[projectId])
                .read(t => t.lastProjectChangeTime)
                .valueOrDefault('');

            const projectLoader = commonUtils.isCacheAllowed()
                ? super.loadCachedProjectModel(
                    altosoft.userId,
                    lastProjectChangeTime,
                    altosoft.globalModelTime,
                    altosoft.locale,
                    altosoft.version,
                    projectId
                )
                : super.loadProjectModel(projectId);

            return projectLoader.then(model => {
                altosoft.projectDocuments = altosoft.projectDocuments || [];
                _.remove(altosoft.projectDocuments, project => project.id === projectId);
                altosoft.projectDocuments.push(model);
                ColorHelper.resetCache();
            });
        });
    }

    public changePassword(oldPassword: string, newPassword: string): JQueryPromise<any> {
        this.config({
            exceptionText: 'Cannot change password.',
            dontShowErrorDialog: true
        });
        return super.changePassword(oldPassword, newPassword).then(null, error => {
            const e = errorHelper.parseError(error);
            const messageBoxDialog = new MessageBox({
                dialogTitle: strings.loginControl.changePasswordTitle,
                text: e.type === InsightExceptionType.wrongPassword ? strings.loginControl.wrongPassword : strings.loginControl.changePasswordError,
                icon: MessageBoxIcons.warning
            });
            messageBoxDialog.show();
            throw e.type;
        });
    }

    public extractToFormattedExcel(
        projectId: Guid,
        reportId: Guid,
        report: Report,
        viewComponent: ViewComponent,
        gridPrintSettings: GridPrintSettings,
        sendByEmail: boolean,
        emails: string[]
    ): JQueryPromise<Guid> {
        return super.extractToFormattedExcel(projectId, reportId, report, viewComponent, gridPrintSettings, sendByEmail, emails).then(fileId => {
            if (!sendByEmail) {
                urlHelper.downloadFile(fileId);
            }
            return fileId;
        });
    }

    public exportToExcel(projectId: Guid, component: BaseComponent, globalFilters: GroupFilter[], parentIds: Guid[]): JQueryPromise<Guid> {
        return super.exportToExcel(projectId, component, globalFilters, parentIds).then(fileId => {
            urlHelper.downloadFile(fileId);
            return fileId;
        });
    }

    private async startCheckingSession(): Promise<void> {
        while ((altosoft.autoCheckSessionInterval ?? 0) > 0) {
            await Promise.wait(altosoft.autoCheckSessionInterval);
            await this.config({ dontShowErrorDialog: true }).checkSession(constants.guidEmpty);
        }
    }

    private getDataFromService(parameter: DataParameter<ComponentParameter>, requestId): JQueryPromise<ComponentData> {
        const currentTime = new Date();

        return $.Deferred<ComponentData>(def => {
            this.config({ dontShowErrorDialog: true })
                .getData(parameter.projectId, parameter.dataRequestConfiguration as DataRequestConfiguration)
                .then(
                    componentData => {
                        if (_.isNull(parameter.control.root)) {
                            return;
                        }

                        if (componentData == null) {
                            componentData = getEmptyComponentData(parameter.component.id);
                        }
                        const timeSettings = parameter.component.dataDefinition.timeSettings;
                        DataServiceProvider.updateMetricLastData(parameter.projectId, timeSettings, componentData.lastAvailableDate);

                        if (isChartComponent(parameter.component.appearance.componentType) && !parameter.isTrellis) {
                            DataServiceProvider.processTotalsAndAggregations(componentData, parameter.component);
                        }

                        const request = this.getRequestIdentifier(requestId);

                        if (parameter.isLoggingEnabled) {
                            const timeElapsed = new Date().getTime() - currentTime.getTime();
                            commonUtils.log(
                                parameter.component.name + ' loading time: ' + formatHelper.formatMilliseconds(timeElapsed, 'hh:mm:ss.fff')
                            );
                        }
                        if (request || parameter.permanentRequest) {
                            DataServiceProvider.onGetDataCompleted(parameter, componentData);
                            def.resolve(componentData);
                        }
                    },
                    error => def.reject(error)
                );
        }).promise();
    }

    private static updateMetricLastData(projectId: Guid, timeSetting: TimeSettings, lastAvailableDate: Date): void {
        if (timeSetting && timeSetting.chartDateType === ChartDateType.lastToAvailableKpiDate) {
            const metric = documentHelper.getMetric(projectId, timeSetting.metric);
            if (metric && lastAvailableDate) {
                metric.lastAvailableDate = lastAvailableDate;
            }
        }
    }

    private static onGetProcessDataCompleted<T extends { value?: any; lastAvailableDate: Date }>(parameter: IProcessDataParam, componentData: T): T {
        if (componentData.value == null) {
            componentData.value = { processPaths: [], stepsData: [], transitions: [] };
        }
        this.processResults(componentData as any, parameter.configuration.seriesIds, []);
        DataServiceProvider.updateMetricLastData(
            parameter.projectId,
            parameter.configuration.componentDefinition.timeSettings,
            componentData.lastAvailableDate
        );
        return componentData;
    }

    private static onGetDataCompleted(parameter: DataParameter<ComponentParameter>, componentData: ComponentData): JQueryPromise<ComponentData> {
        const seriesIds = _.map(parameter.dataRequestConfiguration.seriesFilters, seriesFilter => seriesFilter.seriesId);
        this.processResults(componentData, seriesIds, parameter.dataRequestConfiguration.drillDownIds, parameter.component, parameter.projectId);
        const appearance = parameter.component.appearance;
        if (
            (isChartComponent(appearance.componentType) || isGridComponent(appearance.componentType)) &&
            appearance.componentType !== ComponentType.trafficLight &&
            appearance.showMarkers
        ) {
            timeHelper.updateTimeSettings(parameter.projectId, parameter.component.dataDefinition.timeSettings);
            const timeInterval = parameter.component.dataDefinition.timeSettings.interval;

            ProcessMarketData.processResults(
                componentData,
                parameter.dataRequestConfiguration.seriesFilters,
                parameter.dataRequestConfiguration.drillDownIds,
                parameter.markerFilterData,
                timeInterval,
                parameter.control.getSeries() as MetricSeries[],
                parameter.control.getDrillDowns(),
                isGridComponent(appearance.componentType)
            );
        }

        return $.Deferred<ComponentData>().resolve(componentData).promise();
    }

    public getDescriptors(projectId: Guid, ...factoryItem: CategorizedInstanceItem<any>[]): JQueryPromise<DocumentDescriptor[]> {
        return this.getDescriptorsByCategoryies(
            projectId,
            _.map(factoryItem, fi => fi.category)
        );
    }

    public getDocument<T extends DocumentBase>(
        projectId: Guid,
        documentId: Guid,
        documentName: string,
        factoryItem: CategorizedInstanceItem<T>
    ): JQueryPromise<T> {
        return this.loadDocument(projectId, documentId, documentName, [factoryItem.category]) as JQueryPromise<T>;
    }

    public tryGetDocument<T extends DocumentBase>(
        projectId: Guid,
        documentId: Guid,
        documentName: string,
        factoryItem: CategorizedInstanceItem<T>
    ): JQueryPromise<T> {
        return this.tryLoadDocument(projectId, documentId, documentName, [factoryItem.category]) as JQueryPromise<T>;
    }

    public getDocuments<T extends DocumentBase>(
        projectId: Guid,
        factoryItem: CategorizedInstanceItem<T>,
        dontShowErrorDialog = false
    ): JQueryPromise<T[]> {
        return this.loadDocumentsByCategory(projectId, [factoryItem.category], dontShowErrorDialog) as JQueryPromise<T[]>;
    }

    public getObject<T extends InsightObject>(objectId: Guid, factoryItem: CategorizedInstanceItem<T>): JQueryPromise<T> {
        return this.getGlobalObject(factoryItem.category, objectId) as JQueryPromise<T>;
    }

    public getObjects<T extends InsightObject>(factoryItem: CategorizedInstanceItem<T>): JQueryPromise<T[]> {
        return this.getAllGlobalObjects([factoryItem.category]) as JQueryPromise<T[]>;
    }

    public uploadFile(
        projectId: Guid,
        uploadData: { $type: string; $value: string } | number[],
        fileName: string,
        appendData: boolean,
        container: StorageContainerType,
        expectEncriptedConnection = false
    ): JQueryPromise<string> {
        return super.uploadFile(projectId, uploadData, fileName, appendData, container, expectEncriptedConnection);
    }

    private static processResults(
        componentData: ComponentData,
        seriesIds: Guid[],
        dimensionIds: string[],
        component: Component = null,
        projectId: Guid = null
    ): void {
        if (!componentData.dataRows) {
            return;
        }
        const rows = componentData.dataRows.slice();
        if (componentData.totalRow) {
            rows.push(componentData.totalRow);
        }
        if (componentData.subAggregations) {
            rows.addRange(componentData.subAggregations);
        }

        _.forEach(rows, row => {
            _.forEach(row.data, (dataCell, index) => {
                if (dataCell.seriesId === undefined) {
                    dataCell.seriesId = seriesIds[index];
                }
            });
            _.forEach(row.dimensions, (dimensionCell, index) => {
                dimensionCell.drillId = dimensionIds[index];
            });
        });
    }

    public static processTotalsAndAggregations(data: ComponentData, component: Component): void {
        const totalName = component.appearance.totalName;
        const showTotals =
            component.dataDefinition.summaryMode === ShowSummaryMode.totals || component.dataDefinition.summaryMode === ShowSummaryMode.both;

        if (showTotals) {
            if (data.totalRow && data.totalRow.data && data.subTotalsRows.length === 0) {
                data.totalRow.dimensions.push({
                    key: constants.chartTotalRowId,
                    value: totalName
                } as any);
                data.dataRows.push(data.totalRow);
            }
        }

        if (data.subAggregations && data.subAggregations.length) {
            const someSeriesHasAggregation = aggregationMode => {
                const inSeries = _.some(
                    component.dataDefinition.series,
                    (series: BaseSeries) =>
                        series.dataDefinition.aggregationMode.hasFlag(aggregationMode) &&
                        series.dataDefinition.referenceLineType === ReferenceLineType.none
                );
                return component.dataDefinition.aggregationMode.hasFlag(aggregationMode) || inSeries;
            };

            _(data.subAggregations)
                .filter(subAggregation => subAggregation.aggregationMode !== SubAggregationMode.none)
                .filter(subAggregation => someSeriesHasAggregation(subAggregation.aggregationMode))
                .forEach(subAggregation => {
                    if (someSeriesHasAggregation(subAggregation.aggregationMode)) {
                        if (subAggregation.dimensions.length) {
                            const dimension = subAggregation.dimensions[subAggregation.dimensions.length - 1];
                            dimension.value = dimension.key = SubAggregationMode.getName(subAggregation.aggregationMode);
                            (dimension as any).isAggregation = true;
                        }
                        data.dataRows.push(subAggregation);
                    }
                });
        }

        if (data.subTotalsRows !== undefined && showTotals) {
            _.forEach(data.subTotalsRows, row => {
                if ((component.appearance as ChartAppearance).showTotalAsPie) {
                    row.dimensions.push({
                        key: constants.chartTotalRowId,
                        value: row.dimensions[0].value
                    } as any);
                } else {
                    row.dimensions.unshift({
                        key: constants.chartTotalRowId,
                        value: totalName
                    } as any);
                }
                (row as any).isSubTotal = true;
            });

            data.dataRows.addRange(data.subTotalsRows);
        }
    }
}

function preprocessModel(model: BaseModel | ViewerModel) {
    model.user.name = model.user.parameters[constants.userPropertyNames.displayName];
    if (!model.user.name) {
        model.user.name = model.user.parameters[constants.userPropertyNames.login];
    }
    if (!model.user.name && !Guid.isGuid(model.user.id, true)) {
        model.user.name = model.user.id;
    }
    const geoDatabaseObject = model.user.geoDatabaseObject;
    if (geoDatabaseObject) {
        geoDatabaseObject.geoLayers = localizationHelper.localizeLayers(geoDatabaseObject.geoLayers, false);
    }

    const datepicker = $.datepicker;
    const locale = localizationHelper.getLocaleForJQueryPicker();

    if ('insightMeta' in model) {
        model.insightMeta.cssStyles = StylesHelper.getClientStyles();
        StylesHelper.includeStyles();
        const { dateTimeFormat } = model.insightMeta;
        if (dateTimeFormat) {
            const firstDayOfWeek = DayOfWeek.getValues()
                .map(i => i.id)
                .indexOf(dateTimeFormat.firstDayOfWeek);

            if (firstDayOfWeek >= 0) {
                datepicker.regional[locale].firstDay = firstDayOfWeek;
            }
        }
    }

    datepicker.setDefaults(datepicker.regional[locale]);

    const timepicker = $.timepicker;
    timepicker.setDefaults(timepicker.regional[locale]);
}

export type CategorizedInstanceItem<T> = AbstractInstanceItem<T> & Categorized;
